/**
 * Application-wide constants.
 *
 * All fixed IDs (seeds from the backend database) and other hardcoded values
 * must be defined here instead of inline in component files.
 */

// ─── Address Field IDs (backend seed) ──────────────────────────────────────

/** Field ID for the State/Province address field. Seed from backend database. */
export const STATE_FIELD_ID = '00000000-0000-0000-0000-000000000010';

/** Field ID for the City address field. Seed from backend database. */
export const CITY_FIELD_ID = '00000000-0000-0000-0000-000000000011';

// ─── Contact Type IDs (backend seed) ────────────────────────────────────────

/** Contact type ID for social network contacts (e.g. LinkedIn, Facebook, Instagram).
 *  Used to separate social contacts from regular contacts (phone, email) within
 *  the same nationality contacts array. */
export const SOCIAL_CONTACT_TYPE_ID = '00000000-0006-0000-0000-000000000003';

/** Contact type ID for voice call contacts (phone). Seed from backend database. */
export const PHONE_CONTACT_TYPE_ID = '00000000-0006-0000-0000-000000000001';

/** Contact type ID for email contacts. Seed from backend database. */
export const EMAIL_CONTACT_TYPE_ID = '00000000-0006-0000-0000-000000000004';

// ─── Contact Field IDs (backend seed) ────────────────────────────────────────

/** Field ID for the mobile phone contact field. Seed from backend database. */
export const PHONE_FIELD_ID = '00000000-0007-0000-0000-000000000002';

/** Field ID for the email contact field. Seed from backend database. */
export const EMAIL_FIELD_ID = '00000000-0000-0000-0000-000000000021';

export const NUMBER_OF_OFFLINE_DAYS_FALLBACK = 30;

// ─── Activity Page Static Data ─────────────────────────────────────────────

/** Static activity rows displayed on the activity history tab.
 *  Will be replaced by API data in a future iteration.
 *  TODO(i18n): mock data — label fields (role/chips/category/since/lastUpdate)
 *  are English literals; translate via keys when this is replaced by API data. */
export const ACTIVITY_STATIC_ROWS = [
  { name: 'Olivia Antunes', role: 'Representing me', date: '02/13/2026', location: 'Lisbon, Portugal', website: 'www.niup.digital', chips: ['Tax Identification Number (TIN)', 'Email', 'Address'] },
  { name: 'Jéssica Lopes', role: 'Me', date: '02/13/2026', location: 'Lisbon, Portugal', website: 'www.niup.com', chips: ['Contact', 'Website', 'Nationality'] },
  { name: 'Olivia Antunes', role: 'Representing me', date: '02/13/2026', location: 'Lisbon, Portugal', website: 'www.niup.digital', chips: ['Tax Identification Number (TIN)', 'Email', 'Address'] },
  { name: 'Jéssica Lopes', role: 'Me', date: '02/13/2026', location: 'Lisbon, Portugal', website: 'www.niup.com', chips: ['Contact', 'Website', 'Nationality'] },
];

/** Static rows for the "Manage data sharing" tab (Activity page).
 *  TODO(i18n): mock data — label fields (role/chips/category/since/lastUpdate)
 *  are English literals; translate via keys when this is replaced by API data. */
export const SHARING_STATIC_ROWS = [
  { website: 'www.niup.digital', since: 'Since Mar 2024', lastUpdate: 'Updated 1 week ago', category: 'Financial data' },
  { website: 'www.niup.com', since: 'Since Mar 2024', lastUpdate: 'Updated 1 week ago', category: 'Contact informations' },
];

// ─── Account Settings Page ──────────────────────────────────────────────────

/** Placeholder "Last updated" date shown on the Account Settings page until the
 *  backend exposes real profile/password update timestamps. ISO string so the
 *  UI can render it in the active locale via `formatDate()`. */
export const ACCOUNT_SETTINGS_LAST_UPDATED_PLACEHOLDER_ISO = '2026-03-01';

// ─── Internationalization (i18n) ────────────────────────────────────────────

/** Default UI locale used when neither the user profile nor the browser
 *  provide a supported language. */
export const DEFAULT_LOCALE = 'en';

/** localStorage key holding a manual locale override. Used mainly for testing
 *  (e.g. `localStorage.setItem('niup.locale', 'pt-BR')`); it wins over the
 *  user profile language when present. */
export const LOCALE_STORAGE_KEY = 'niup.locale';

// ─── List Pagination (on-demand / infinite scroll) ──────────────────────────

/** Rows requested per page by the people lists. Sent as `pageSize` in the
 *  `Associations/list/*` POST body (the backend forwards the body untouched). */
export const PEOPLE_PAGE_SIZE = 20;

/** Rows requested per page by the permission group lists. Sent as `pageSize` in
 *  the `ShareGroups/list` POST body. */
export const GROUPS_PAGE_SIZE = 20;

/** Rows requested per page by the companies (collective association) lists. Sent as
 *  `pageSize` in the `Associations/list/*` POST body. */
export const COMPANIES_PAGE_SIZE = 20;

/** Rows requested per page by the activity history list. Sent as `pageSize` in the
 *  `Activities/list/{niupId}` POST body. If the upstream endpoint ignores paging it
 *  simply returns everything on page 1 and the list stops there — no breakage. */
export const ACTIVITIES_PAGE_SIZE = 20;

/** Rows requested per page by the OAuth2 application list. Sent as `pageSize` in
 *  the `Applications/list` POST body. */
export const APPLICATIONS_PAGE_SIZE = 20;

/** Rows revealed per page by the notification lists. The notification endpoint is a
 *  GET that always returns the full feed, so this windows the loaded array
 *  client-side instead of paging the server. */
export const NOTIFICATIONS_PAGE_SIZE = 15;

/** How far ahead of the viewport `niup-infinite-scroll` starts fetching the next
 *  page. Expressed in CSS margin syntax (IntersectionObserver `rootMargin`);
 *  a larger value prefetches earlier and hides the loading hint from the user. */
export const INFINITE_SCROLL_ROOT_MARGIN = '240px';

// ─── Administrative Resources (profile tab) ─────────────────────────────────

/** Tools listed in the "Administrative Resources" tab of a profile — company or
 *  the signed-in user's own individual profile
 *  (niup-admin-resources). `labelKey`/`descriptionKey` are i18n dictionary keys
 *  (see locales/en/pages.ts) — render them with `t()`. `key` is the value of the
 *  `?tool=` query param that opens the tool.
 *  Flat, ungrouped list: a new tool is one more entry here, nothing else changes. */
export const ADMIN_RESOURCE_TOOLS = [
  {
    key: 'oauth2-credentials',
    labelKey: 'pages.adminResources.tools.oauth2Credentials.label',
    descriptionKey: 'pages.adminResources.tools.oauth2Credentials.description',
    icon: '/assets/svg/icon-key.svg',
  },
];

// ─── Legal Page Tabs ────────────────────────────────────────────────────────

/** Tabs for the Terms & Conditions / Privacy / Cookies legal page.
 *  `labelKey` is an i18n dictionary key (see locales/en/legal.ts); render it
 *  with `t(tab.labelKey)` so the label follows the active locale. */
export const LEGAL_PAGE_TABS = [
  { key: 'terms', labelKey: 'legal.tabs.terms', href: '/terms' },
  { key: 'privacy', labelKey: 'legal.tabs.privacy', href: '/privacy' },
  { key: 'cookies', labelKey: 'legal.tabs.cookies', href: '/cookies' },
];
